import { useContext, useEffect, useState } from 'react';

import {
	Button,
	Card,
	Flex,
	Input,
	List,
	Pagination,
	Space,
	Spin,
	Tooltip,
	Typography,
} from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { SubscriptionContext } from '@/App';
import { useGetCategories } from '@/api/monitor_collections';
import VideoCard from '@/components/common/VideoCard';
import { SUBSCRIPTIONS_TYPES_ENUM } from '@/const';
import { useDebounce } from '@/hooks';
import { useOpenSearch } from '@/hooks/useOpenSearch';
import { Video, convertOpenSearchToVideo } from '@/interfaces/video';
import placeholderImg from '@/lib/assets/placeholder.jpg';
import { formatSecondsToTime } from '@/lib/helpers/formatSecondsToTime';
import getReadableCron from '@/lib/helpers/getReadableCron';
import hasSubscriptionType from '@/lib/helpers/hasSubscriptionType';
import prepareDate from '@/lib/helpers/prepareDate';
import prepareImageSrc from '@/lib/helpers/prepareImageSrc';
import { PageContext } from '@/providers/PageContextProvider';
import { IPagination, TSortBy, TSortOrder } from '@/ts';

import styles from './style.module.css';

import FiltersSection from '../video-insights-co-pilot/FiltersSection';

export default function VideoDataLake() {
	const { setPageTitle, setIsCreditsCounterVisible } = useContext(PageContext);
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const { subscriptions, subscriptionLoading } =
		useContext(SubscriptionContext);

	const [pagination, setPagination] = useState<IPagination>({
		page: Number(searchParams.get('page') ?? 1),
		pageCount: 1,
		total: 0,
		limit: Number(searchParams.get('limit') ?? 9),
	});
	const [search, setSearch] = useState(searchParams.get('search') ?? '');
	const [sortBy, setSortBy] = useState<TSortBy>('name');
	const [sortOrder, setSortOrder] = useState<TSortOrder>('asc');
	const [appliedData, setAppliedData] = useState<any>(null);
	const [isPanelOpen, setIsPanelOpen] = useState(false);

	// Cross-category search state
	const [isFiltersApplied, setIsFiltersApplied] = useState(false);
	const [searchResults, setSearchResults] = useState<Video[]>([]);
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [searchEntities, setSearchEntities] = useState<string[]>([]);

	// Pagination state for search results
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 10;
	const [totalResults, setTotalResults] = useState(0);

	const debouncedSearch = useDebounce(search);

	// OpenSearch integration
	const {
		results: opensearchResults,
		loading: opensearchLoading,
		error: opensearchError,
		hybridSearch,
		clearResults,
		checkHealth,
		isHealthy,
	} = useOpenSearch();

	const { data, isLoading, isFetching, refetch } = useGetCategories({
		pagination,
		sortBy,
		sortOrder,
		search: debouncedSearch.length >= 3 ? debouncedSearch : '',
	});

	useEffect(() => {
		setPageTitle('Video Intelligence Panel');
		setIsCreditsCounterVisible(false);

		// Check OpenSearch API health on component mount
		checkHealth();

		return () => {
			setPageTitle('');
			setIsCreditsCounterVisible(true);
		};
	}, [checkHealth]);

	useEffect(() => {
		const pageParam = searchParams.get('page');
		const searchParam = searchParams.get('search');

		// const limitParam = searchParams.get('limit');

		setSearchParams({
			page: pageParam ?? pagination?.page?.toString(),
			search: searchParam ?? '',
			// limit: limitParam ?? pagination?.limit?.toString(),
		});
	}, []);

	useEffect(() => {
		if (!subscriptionLoading && subscriptions && subscriptions?.length > 0) {
			if (
				!hasSubscriptionType(subscriptions, SUBSCRIPTIONS_TYPES_ENUM.datalake)
			) {
				navigate('/app/dashboard/product-main-menu');
			}
		} else if (
			!subscriptionLoading &&
			subscriptions &&
			subscriptions?.length === 0
		) {
			navigate('/app/dashboard/product-main-menu');
		}
	}, [subscriptions, subscriptionLoading]);

	const items = data?.data?.data?.items ?? [];
	const paginationData = data?.data?.data?.pagination;

	// Handle cross-category hybrid search
	const handleCrossCategorySearch = async (
		params: {
			semantic_query: string;
			keyword_query?: string;
			entity_query?: string;
			date_range?: { from_date: string; to_date: string };
			analysis_date_range?: { from_date: string; to_date: string };
			platform?: string;
			language?: string;
			reach?: string;
			sentiment?: string;
			entity_logic?: string;
		},
		page: number = 1,
	) => {
		if (!params.semantic_query.trim()) {
			setSearchResults([]);
			setIsFiltersApplied(false);
			setTotalResults(0);
			setCurrentPage(1);
			return;
		}

		// Check if this is a new search by comparing with current search parameters
		const isNewSearch = params.semantic_query !== searchTerm;

		// Only reset to page 1 if this is a new search
		if (isNewSearch) {
			setCurrentPage(1);
			page = 1;
		} else {
			// For pagination, update the current page
			setCurrentPage(page);
		}

		setSearchTerm(params.semantic_query);
		setSearchEntities(
			params.entity_query
				? params.entity_query.split(',').map((e) => e.trim())
				: [],
		);
		setIsFiltersApplied(true);

		try {
			// Map category names to correct index names (matching actual category names)
			const categoryToIndexMap: Record<string, string> = {
				'AI Influencers (US)': 'ai-influencers-us-transcripts',
				'Tech influencers (US)': 'tech-influencers-transcripts',
				'Politics (US)': 'politics-us-transcripts',
				'US news': 'us-news-transcripts',
				'Generalist (North America)': 'generalist-north-america-transcripts',
				Skincare: 'skincare-transcripts',
			};

			// Get all available category names and map to indexes
			const availableCategoryNames =
				data?.data?.data?.items
					?.map((item: any) => item?.name)
					.filter(Boolean) || [];
			const mappedIndexes = availableCategoryNames
				.map((categoryName: string) => categoryToIndexMap[categoryName])
				.filter(Boolean); // Remove undefined values

			// Use only the mapped indexes (no fallback)
			const indexPattern = mappedIndexes.join(',');

			console.log('🔍 Cross-category search:', {
				availableCategories: availableCategoryNames,
				mappedIndexes,
				indexPattern,
			});

			// Calculate offset for pagination
			const from = (page - 1) * itemsPerPage;

			// Use hybrid search across ONLY the real category indexes
			await hybridSearch({
				semantic_query: params.semantic_query,
				keyword_query: params.keyword_query,
				entity_query: params.entity_query,
				size: itemsPerPage,
				from: from,
				index_pattern: indexPattern, // Search only real category indexes
				from_date: params.date_range?.from_date,
				to_date: params.date_range?.to_date,
				analysis_from_date: params.analysis_date_range?.from_date,
				analysis_to_date: params.analysis_date_range?.to_date,
				platform: params.platform,
				language: params.language,
				reach: params.reach,
				sentiment: params.sentiment,
				entity_logic: params.entity_logic,
			});
		} catch (error) {
			console.error('Cross-category search failed:', error);
		}
	};

	// Convert OpenSearch results to Video format when results change
	useEffect(() => {
		if (opensearchResults?.results) {
			const convertedVideos = opensearchResults.results.map(
				convertOpenSearchToVideo,
			);
			setSearchResults(convertedVideos);

			// Always update total results from the search results
			setTotalResults(opensearchResults.total || 0);
		}
	}, [opensearchResults]);

	// Pagination logic for search results
	const totalPages = Math.ceil(totalResults / itemsPerPage);

	// Pagination handlers
	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		// Fetch new results for the selected page
		if (searchTerm) {
			// Re-trigger search with new page
			handleCrossCategorySearch(
				{
					semantic_query: searchTerm,
					keyword_query: appliedData?.filter_data?.keywords?.join(', '),
					entity_query: appliedData?.filter_data?.brands?.join(', '),
					date_range: appliedData?.filter_data?.date_range,
					analysis_date_range: appliedData?.filter_data?.analysis_date_range,
					platform: appliedData?.filter_data?.platform,
					language: appliedData?.filter_data?.language,
					reach: appliedData?.filter_data?.reach,
					sentiment: appliedData?.filter_data?.sentiment,
					entity_logic: appliedData?.filter_data?.entity_logic,
				},
				page,
			);
		}
		// Scroll to top of results
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	return (
		<Flex vertical gap={20} style={{ width: '100%', paddingBottom: '2.5rem' }}>
			<h2 style={{ margin: 0 }}>
				Subscribed Video Intelligence Panel categories
			</h2>

			<Flex style={{ width: '100%' }}>
				{!isPanelOpen && (
					<Input
						placeholder="Search for category name"
						value={search}
						onChange={(e) => {
							const value = e.target.value;
							setSearchParams((searchParams) => {
								searchParams.set('search', value ? value.toString() : '');
								return searchParams;
							});
							setSearch(value);
						}}
						readOnly={isLoading || isFetching}
						style={{ width: '100%', maxWidth: '644px' }}
					/>
				)}

				<Flex gap={12} align="center" style={{ marginLeft: 'auto' }}>
					<Button onClick={() => setIsPanelOpen((prev) => !prev)}>
						{isPanelOpen ? 'Hide' : 'Show'} Q&A panel
					</Button>
				</Flex>
			</Flex>

			<FiltersSection
				key={appliedData?.id || 'filters-section-component'}
				isOpen={isPanelOpen}
				data={appliedData}
				clearData={() => setAppliedData(null)}
				onHybridSearch={handleCrossCategorySearch}
			/>

			{(isFetching || isLoading) && (
				<Flex
					style={{ width: '100%', height: '300px' }}
					justify="center"
					align="center"
				>
					<Spin />
				</Flex>
			)}

			{!isFetching && (
				<>
					{items?.length === 0 ? (
						<Typography.Text type="secondary">
							No categories found. Please subscribe to some categories.
						</Typography.Text>
					) : (
						<Flex vertical gap={16} align="center">
							<ul className={styles.itemsList}>
								{items.map((item, index) => (
									<li key={index} className={styles.itemsListItem}>
										<Button
											type="text"
											onClick={() => {
												navigate(
													`/app/dashboard/video-intelligence-lake/${item?.id}`,
												);
												localStorage.setItem(
													'category_page',
													pagination.page?.toString(),
												);
											}}
											className={styles.itemLink}
											style={{
												height: 'unset',
												width: '100%',
												padding: 8,
												display: 'flex',
												justifyContent: 'flex-start',
												alignItems: 'stretch',
												gap: 16,
												textAlign: 'left',
											}}
										>
											<div className={styles.imageWrapper}>
												{item?.image_url ? (
													<img
														alt="example"
														src={prepareImageSrc(item?.image_url)}
														width={220}
														height={'auto'}
														onError={(e) => {
															e.currentTarget.src = placeholderImg;
														}}
													/>
												) : (
													<img
														alt="Category"
														src={placeholderImg}
														width={220}
														height={220}
														style={{ objectFit: 'cover' }}
													/>
												)}
											</div>

											<Flex vertical className={styles.metaWrapper}>
												<Flex vertical gap={8}>
													{/* <Typography.Text style={{ color: 'gray' }}>
														{prepareDate(item?.created_at?.toString() ?? '')}
													</Typography.Text> */}
													<Typography.Title
														level={5}
														style={{
															margin: 0,
															width: '100%',
															whiteSpace: 'normal',
														}}
													>
														{item?.name}
													</Typography.Title>
												</Flex>
												{item?.description && (
													// <div style={{ overflow: 'hidden' }}>
													<Typography.Text
														ellipsis
														style={{
															margin: 0,
															width: '100%',
															whiteSpace: 'normal',
															// maxHeight: '200px',
														}}
														// className={styles.truncate}
													>
														{/* {item?.description} */}
														{item?.description?.length > 120 ? (
															<Tooltip
																title={item?.description}
																placement="bottom"
															>
																<span>
																	{item?.description?.slice(0, 120)}...
																</span>
															</Tooltip>
														) : (
															item?.description
														)}
													</Typography.Text>
													// </div>
												)}
												<Space style={{ fontSize: '14px' }} wrap>
													{/* {item?.creators_amount
														? `${item?.creators_amount} account${
																Number(item?.creators_amount) > 1 ? 's' : ''
														  }.`
														: ''}
													{item?.video_duration
														? `${formatSecondsToTime(
																Number(item?.video_duration),
														  )} of content.`
														: ''} */}
													<span>Updated {getReadableCron(item?.cron)}.</span>
												</Space>
											</Flex>
										</Button>
									</li>
								))}
							</ul>
							<Pagination
								// showSizeChanger={false}
								pageSize={paginationData?.limit ?? 9}
								pageSizeOptions={[9, 18, 36]}
								defaultCurrent={paginationData?.page ?? 1}
								total={paginationData?.total ?? 0}
								onChange={(page, pageSize) => {
									console.log('page', page);

									setPagination((cur) => ({ ...cur, page, limit: pageSize }));
									setSearchParams((searchParams) => {
										searchParams.set('page', page.toString());
										// searchParams.set('limit', pageSize.toString());
										return searchParams;
									});
								}}
							/>
						</Flex>
					)}
				</>
			)}

			{/* Video Cards Section - Show when cross-category search is applied */}
			{isFiltersApplied && (
				<Flex vertical gap={16} style={{ width: '100%' }}>
					{/* Test: Index Results Breakdown */}
					{opensearchResults?.index_counts && (
						<Flex
							justify="center"
							align="center"
							style={{ marginBottom: '16px' }}
						>
							<Typography.Text type="secondary" style={{ fontSize: '12px' }}>
								Test - Results by index:{' '}
								{Object.entries(opensearchResults.index_counts)
									.map(([index, count]) => `${index}: ${count}`)
									.join(', ')}
							</Typography.Text>
						</Flex>
					)}

					{/* Pagination Controls */}
					{totalResults > 0 && totalPages > 1 && (
						<Flex
							justify="center"
							align="center"
							style={{ marginBottom: '16px' }}
						>
							<Typography.Text type="secondary" style={{ marginRight: '16px' }}>
								{totalResults} videos found across all categories
							</Typography.Text>
							<Pagination
								current={currentPage}
								total={totalResults}
								pageSize={itemsPerPage}
								showSizeChanger={false}
								showQuickJumper={false}
								showTotal={(total, range) =>
									`${range[0]}-${range[1]} of ${total}`
								}
								onChange={handlePageChange}
								size="small"
								disabled={opensearchLoading}
							/>
						</Flex>
					)}

					{/* Error Display */}
					{opensearchError && (
						<Flex
							justify="center"
							align="center"
							style={{ marginBottom: '16px' }}
						>
							<Typography.Text type="danger">
								Error: {opensearchError}
							</Typography.Text>
						</Flex>
					)}

					{opensearchLoading ? (
						<Flex
							justify="center"
							align="center"
							style={{ minHeight: '200px' }}
						>
							<Spin size="large" />
						</Flex>
					) : searchResults.length > 0 ? (
						<Flex vertical align="center" gap={16} style={{ width: '100%' }}>
							{searchResults.map((video) => (
								<VideoCard
									key={video._id}
									video={video}
									searchTerm={searchTerm}
									searchEntities={searchEntities}
								/>
							))}
						</Flex>
					) : (
						<Flex
							justify="center"
							align="center"
							style={{ minHeight: '200px' }}
						>
							<Typography.Text type="secondary">
								No results found for "{searchTerm}" across all categories
							</Typography.Text>
						</Flex>
					)}
				</Flex>
			)}
		</Flex>
	);
}
